import React from 'react';

import { merge } from 'es-toolkit/compat';

import { cleanStringFromNonDigits } from '@hrlogics/helper-utils';

import type { Nullable } from '@clearverify/types';

import { useVerifierStaffListQuery, VerifierStaffWhere } from 'src/api';
import { useFilterStorage, useSearchQueryVariables } from 'src/hooks/common';
import { getUserFullNameFilter } from 'src/utils/user';

import { useStatusTableFilter } from '../filters';

export const useVerifierStaffList = (
  verifierId: Nullable<string>,
  customFilterQueryVariables?: Nullable<VerifierStaffWhere>,
  notifyOnNetworkStatusChange?: boolean,
) => {
  const searchQueryVariables = useSearchQueryVariables(prepareSearchFilter);

  const { sortQueryVariable, paginationQueryVariables } = useFilterStorage();

  const filterByVerifierId = React.useMemo(
    () => ({
      verifierId: { equals: verifierId },
    }),
    [verifierId],
  );

  const { filterByUserStatus } = useStatusTableFilter();

  const { data, loading, refetch } = useVerifierStaffListQuery({
    variables: {
      filter: merge(
        {},
        searchQueryVariables,
        filterByVerifierId,
        customFilterQueryVariables ?? filterByUserStatus,
      ),
      ...paginationQueryVariables,
      orderBy: sortQueryVariable,
    },
    notifyOnNetworkStatusChange,
  });

  const verifierStaffList = React.useMemo(() => {
    return data?.verifierStaffList ?? [];
  }, [data?.verifierStaffList]);

  const verifierStaffCount = React.useMemo(() => {
    return data?.verifierStaffCount || 0;
  }, [data?.verifierStaffCount]);

  return {
    verifierStaffList,
    verifierStaffCount,
    refetch,
    loading,
  };
};

const prepareSearchFilter = (searchValue: Nullable<string>): VerifierStaffWhere =>
  searchValue && searchValue.length > 0
    ? {
        user: {
          OR: [
            {
              employee: {
                user: getUserFullNameFilter(searchValue),
              },
            },
            { email: { contains: searchValue.toLowerCase() } },
            {
              phoneNumber: { contains: cleanStringFromNonDigits(searchValue) },
            },
          ],
        },
      }
    : {};
